import { useEffect, useRef, useState } from "react";
import { buildPreviewConstraints, type MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import { evaluateMediaSupport, MediaUnavailableError, mediaSupportCode } from "../../services/mediaSupport";
import { useI18n } from "../../i18n/localization";
import { MaterialMediaIcon } from "./MaterialMediaIcon";

// Post-readiness local preview shown on the waiting (traffic) and directory (field) screens.
// It re-acquires a fresh preview with the devices chosen in readiness (NOT another readiness
// step / no device pickers), shows the local camera, and exposes a microphone on/off control.
// Its tracks are always stopped on unmount so it never contends with the in-call media client
// that CallScreen starts once a call connects.
function toPreviewError(e: unknown, noDevice: string, messageFor: (code: string) => string): string {
  if (e instanceof MediaUnavailableError) return messageFor(mediaSupportCode(e.support));
  if (e instanceof DOMException) {
    if (e.name === "NotAllowedError" || e.name === "SecurityError") return messageFor("VC-MEDIA-001");
    if (e.name === "NotFoundError" || e.name === "OverconstrainedError") return noDevice;
    return messageFor("VC-SYS-001");
  }
  return messageFor("VC-SYS-001");
}

export function LocalWaitingPreview({
  prefs,
  onMicToggle
}: {
  prefs: MediaDevicePreferences;
  onMicToggle: (next: boolean) => void;
}) {
  const { t, dir, messageFor } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [micOn, setMicOn] = useState(prefs.micEnabled);
  const [error, setError] = useState<string | null>(null);

  // Acquire the preview once for the chosen devices. Deps cover the readiness selection so a
  // (rare) change re-acquires; the mic toggle flips track.enabled directly without re-acquiring.
  useEffect(() => {
    let cancelled = false;
    const support = evaluateMediaSupport();
    if (support !== "ok") {
      setError(messageFor(mediaSupportCode(support)));
      return;
    }
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          buildPreviewConstraints({ cameraDeviceId: prefs.cameraDeviceId, micDeviceId: prefs.micDeviceId })
        );
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const vt = stream.getVideoTracks()[0];
        if (vt) vt.enabled = prefs.cameraEnabled;
        const at = stream.getAudioTracks()[0];
        if (at) at.enabled = micOn;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setError(null);
      } catch (e) {
        if (!cancelled) setError(toPreviewError(e, t.readiness.noDevice, messageFor));
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // micOn is intentionally excluded: toggling must not re-acquire the stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.cameraDeviceId, prefs.micDeviceId, prefs.cameraEnabled]);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    const at = streamRef.current?.getAudioTracks()[0];
    if (at) at.enabled = next;
    onMicToggle(next); // carry into prefs.micEnabled → CallScreen initial state
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} dir={dir}>
      <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", maxHeight: 260, background: "#0f1e3a", borderRadius: 10, overflow: "hidden" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", opacity: prefs.cameraEnabled && !error ? 1 : 0 }}
        />
        {!prefs.cameraEnabled && !error && <div style={overlayCentre}>{t.readiness.cameraOffPreview}</div>}
        {error && <div style={{ ...overlayCentre, padding: 16, textAlign: "center", fontSize: 13 }}>{error}</div>}
        {/* Neutral local-preview chip — NOT a recording indicator (the call has not started). */}
        <span style={selfBadge}>{t.call.localPreview}</span>
      </div>
      <button
        type="button"
        onClick={toggleMic}
        aria-label={micOn ? t.readiness.micOn : t.readiness.micOff}
        aria-pressed={micOn}
        title={micOn ? t.readiness.micOn : t.readiness.micOff}
        style={micBtn(micOn)}
      >
        <MaterialMediaIcon name={micOn ? "mic" : "mic_off"} />
      </button>
    </div>
  );
}

const overlayCentre: React.CSSProperties = {
  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
  color: "#dbe7ff", fontSize: 14, background: "rgba(15,30,58,0.35)"
};
const selfBadge: React.CSSProperties = {
  position: "absolute", top: 8, insetInlineEnd: 8, color: "#dbe7ff", fontSize: 11,
  background: "rgba(20,48,95,0.75)", padding: "2px 10px", borderRadius: 999
};
function micBtn(on: boolean): React.CSSProperties {
  return {
    background: on ? "#1d4ed8" : "#e2e8f0", color: on ? "#fff" : "#1e2a3a",
    border: "none", borderRadius: 6, width: 44, height: 38, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, cursor: "pointer", alignSelf: "flex-start"
  };
}
